/** Configuration → Documents — option lists, helpers, and sample rules */

export const DOCUMENT_MODULE_OPTIONS = [
  { value: 'compliance', label: 'Compliance' },
  { value: 'messages', label: 'Messages' },
  { value: 'quotes', label: 'Quotes' },
  { value: 'support', label: 'Support Tickets' },
  { value: 'moderation', label: 'Moderation' },
  { value: 'fraud_security', label: 'Fraud & Security' },
];

/** @deprecated use DOCUMENT_MODULE_OPTIONS */
export const DOCUMENT_RULE_AREA_OPTIONS = DOCUMENT_MODULE_OPTIONS;

/** Entry points by module — values stable for storage */
export const ENTRY_POINTS_BY_MODULE = {
  compliance: [
    { value: 'compliance_application_upload', label: 'Compliance Application Upload' },
    { value: 'compliance_update_upload', label: 'Compliance Update Upload' },
    { value: 'compliance_renewal_upload', label: 'Compliance Renewal Upload' },
  ],
  messages: [{ value: 'message_attachment_upload', label: 'Message Attachment Upload' }],
  quotes: [{ value: 'quote_request_upload', label: 'Quote Request Upload' }],
  support: [{ value: 'support_ticket_reply_upload', label: 'Support Ticket Reply Upload' }],
  moderation: [{ value: 'moderation_evidence_upload', label: 'Moderation Evidence Upload' }],
  fraud_security: [
    { value: 'fraud_evidence_upload', label: 'Fraud Evidence Upload' },
    { value: 'security_evidence_upload', label: 'Security Evidence Upload' },
  ],
};

const FLAT_ENTRY_OPTIONS = Object.values(ENTRY_POINTS_BY_MODULE).flat();

export function getEntryPointOptionsForModule(moduleValue) {
  if (!moduleValue) return [];
  return ENTRY_POINTS_BY_MODULE[moduleValue] || [];
}

export function labelEntryPoint(value) {
  if (value == null || value === '') return '';
  return FLAT_ENTRY_OPTIONS.find((o) => o.value === value)?.label ?? String(value);
}

/** @deprecated use getEntryPointOptionsForModule(module) — flat list retained for migration */
export const ENTRY_POINT_OPTIONS = FLAT_ENTRY_OPTIONS;

export const FILE_FORMAT_OPTIONS = [
  { value: 'pdf', label: 'PDF' },
  { value: 'jpg', label: 'JPG' },
  { value: 'jpeg', label: 'JPEG' },
  { value: 'webp', label: 'WebP' },
];

/** Failed check — spec section 12 */
export const FAILED_DOCUMENT_RESULT_OPTIONS = [
  { value: 'require_resubmission', label: 'Require Resubmission' },
  { value: 'mark_document_incomplete', label: 'Mark Document Incomplete' },
  { value: 'hold_related_item', label: 'Hold Related Item' },
];

/** @deprecated use FAILED_DOCUMENT_RESULT_OPTIONS */
export const REJECTED_DOCUMENT_RESULT_OPTIONS = FAILED_DOCUMENT_RESULT_OPTIONS;

export const EXPIRED_DOCUMENT_RESULT_OPTIONS = [
  { value: 'mark_document_incomplete', label: 'Mark Document Incomplete' },
  { value: 'require_replacement', label: 'Require Replacement' },
  { value: 'hold_related_item', label: 'Hold Related Item' },
];

export const EXPIRY_REMINDER_TIMING_OPTIONS = [
  { value: '30_days_before', label: '30 Days Before' },
  { value: '14_days_before', label: '14 Days Before' },
  { value: '7_days_before', label: '7 Days Before' },
  { value: 'on_expiry_date', label: 'On Expiry Date' },
];

export const REPLACEMENT_RULE_OPTIONS = [
  { value: 'replace_current_document', label: 'Replace Current Document' },
  { value: 'add_additional_document', label: 'Add Additional Document' },
];

export const MISSING_DOCUMENT_RESULT_OPTIONS = [
  { value: 'no_action', label: 'No Action' },
  { value: 'show_prompt', label: 'Show Prompt' },
  { value: 'mark_document_incomplete', label: 'Mark Document Incomplete' },
  { value: 'hold_related_item', label: 'Hold Related Item' },
];

export const INVALID_DOCUMENT_RESULT_OPTIONS = [
  { value: 'show_prompt', label: 'Show Prompt' },
  { value: 'mark_document_incomplete', label: 'Mark Document Incomplete' },
  { value: 'require_resubmission', label: 'Require Resubmission' },
  { value: 'hold_related_item', label: 'Hold Related Item' },
];

const LEGACY_ENTRY_POINT_MAP = {
  compliance_section: 'compliance_application_upload',
  business_dashboard: 'compliance_application_upload',
  compliance_upload: 'compliance_application_upload',
  support_ticket: 'support_ticket_reply_upload',
  support_ticket_upload: 'support_ticket_reply_upload',
  moderation_flow: 'moderation_evidence_upload',
  fraud_review: 'fraud_evidence_upload',
};

function normalizeEntryPoint(v) {
  if (!v) return '';
  return LEGACY_ENTRY_POINT_MAP[v] || v;
}

/** Map legacy formats (e.g. PNG/HEIC) to current allowed set */
function migrateAllowedFormats(arr) {
  if (!Array.isArray(arr)) return [];
  const set = new Set();
  for (const f of arr) {
    if (f === 'png' || f === 'heic') {
      if (FILE_FORMAT_OPTIONS.some((o) => o.value === 'jpeg')) set.add('jpeg');
    } else if (FILE_FORMAT_OPTIONS.some((o) => o.value === f)) {
      set.add(f);
    }
  }
  return [...set];
}

function normalizeReplacementMethod(m) {
  if (!m) return '';
  if (m === 'replace_current_file') return 'replace_current_document';
  if (
    m === 'add_new_document' ||
    m === 'add_new_document_review' ||
    m === 'add_new_and_review'
  ) {
    return 'add_additional_document';
  }
  return m;
}

function normalizeHoldToken(v) {
  if (v === 'hold_related_process') return 'hold_related_item';
  return v;
}

function legacyMultiToSingle(arr, legacyMap, allowedValues) {
  const allowed = new Set(allowedValues);
  if (!Array.isArray(arr) || arr.length === 0) return '';
  for (const raw of arr) {
    const mapped = legacyMap[raw];
    const token = mapped !== undefined && mapped !== '' ? normalizeHoldToken(mapped) : normalizeHoldToken(raw);
    if (token && allowed.has(token)) return token;
  }
  return '';
}

function coalesceSingleField(raw, legacyArrays, legacyMap, optionValues) {
  const allowed = optionValues;
  const merged = [];
  if (raw != null && raw !== '') {
    if (Array.isArray(raw)) merged.push(...raw);
    else {
      const v = normalizeHoldToken(raw);
      if (allowed.includes(v)) return v;
    }
  }
  legacyArrays.forEach((a) => {
    if (Array.isArray(a)) merged.push(...a);
  });
  return merged.length ? legacyMultiToSingle(merged, legacyMap, allowed) : '';
}

const LEGACY_FAILED = {
  mark_incomplete: 'mark_document_incomplete',
  restrict_related_action: 'hold_related_item',
  route_for_review: '',
};

const LEGACY_EXPIRED = {
  mark_incomplete: 'mark_document_incomplete',
  restrict_related_action: 'hold_related_item',
  show_prompt_only: '',
};

const LEGACY_MISSING = {
  mark_incomplete: 'mark_document_incomplete',
  restrict_related_flow: 'hold_related_item',
  restrict_publish: 'hold_related_item',
  restrict_quote_eligibility: 'hold_related_item',
};

const LEGACY_INVALID = {
  mark_incomplete: 'mark_document_incomplete',
  restrict_related_action: 'hold_related_item',
  route_for_review: 'hold_related_item',
};

export function labelFromOptions(options, value) {
  if (value == null || value === '') return '';
  return options.find((o) => o.value === value)?.label ?? String(value);
}

export function getDocumentModuleLabel(value) {
  return labelFromOptions(DOCUMENT_MODULE_OPTIONS, value);
}

/** @deprecated use getDocumentModuleLabel */
export function getDocumentRuleAreaLabel(value) {
  return getDocumentModuleLabel(value);
}

/** Main table — Upload Rules column */
export function formatUploadRulesSummary(rule) {
  if (!rule?.uploadAllowed) return 'Upload not allowed';
  const labels = (rule.allowedFileFormats || [])
    .map((v) => labelFromOptions(FILE_FORMAT_OPTIONS, v))
    .filter(Boolean);
  const fmts = labels.length ? labels.join(', ') : '—';
  const n = Number(rule.maximumFileCount);
  const count = Number.isFinite(n) && n > 0 ? n : 1;
  const files = count === 1 ? '1 file' : `up to ${count} files`;
  const mb = rule.maximumFileSize?.toString()?.trim() || '—';
  return `${fmts}, ${files}, ${mb} MB max`;
}

/** Main table — Check Rules column */
export function formatCheckRulesSummary(rule) {
  const checkReq = Boolean(rule.checkRequired ?? rule.reviewRequired);
  const outcomeReq = Boolean(rule.checkOutcomeRequired ?? rule.approvalRequired);
  const chk = checkReq ? 'Check required' : 'Check optional';
  const oc = outcomeReq ? ', check outcome required' : ', check outcome not required';
  return chk + oc;
}

/** Main table — Expiry Rules column */
export function formatExpiryRulesSummary(rule) {
  if (!rule?.expiryApplies) {
    if (rule?.replacementAllowed) return 'No expiry, replacement optional';
    return 'No expiry, replacement not used';
  }
  return rule?.replacementAllowed ? 'Expiry applies, replacement allowed' : 'Expiry applies, replacement not allowed';
}

/** Table narrative for Document Result column (prefers persisted `documentResult` when set) */
export function formatDocumentResultColumn(rule) {
  if (rule?.documentResult?.trim()) return rule.documentResult.trim();
  const parts = [];
  const m = labelFromOptions(MISSING_DOCUMENT_RESULT_OPTIONS, rule.missingDocumentResult);
  if (m) parts.push(`Missing: ${m}`);
  const inv = labelFromOptions(INVALID_DOCUMENT_RESULT_OPTIONS, rule.invalidDocumentResult);
  if (inv) parts.push(`Invalid: ${inv}`);
  const failedVals = FAILED_DOCUMENT_RESULT_OPTIONS.map((o) => o.value);
  const failedRaw = rule.failedDocumentResult ?? rule.rejectedDocumentResult;
  const fv = labelFromOptions(FAILED_DOCUMENT_RESULT_OPTIONS, failedRaw);
  if (fv && failedVals.includes(failedRaw)) parts.push(`Failed: ${fv}`);
  if (rule.expiryApplies) {
    const ex = labelFromOptions(EXPIRED_DOCUMENT_RESULT_OPTIONS, rule.expiredDocumentResult);
    if (ex) parts.push(`Expired: ${ex}`);
  }
  return parts.length ? parts.join(' · ') : '—';
}

export function normalizeDocumentRuleForForm(rule) {
  if (!rule) return null;

  const expiredVals = EXPIRED_DOCUMENT_RESULT_OPTIONS.map((o) => o.value);
  const failedVals = FAILED_DOCUMENT_RESULT_OPTIONS.map((o) => o.value);
  const missingVals = MISSING_DOCUMENT_RESULT_OPTIONS.map((o) => o.value);
  const invalidVals = INVALID_DOCUMENT_RESULT_OPTIONS.map((o) => o.value);

  const expiredDocumentResult = coalesceSingleField(
    rule.expiredDocumentResult,
    [rule.ifExpiredRule, rule.ifExpiredOutcome],
    LEGACY_EXPIRED,
    expiredVals,
  );

  let failedDocumentResult = coalesceSingleField(
    rule.failedDocumentResult ?? rule.rejectedDocumentResult,
    [rule.ifRejectedReview],
    LEGACY_FAILED,
    failedVals,
  );

  const missingDocumentResult = coalesceSingleField(
    rule.missingDocumentResult,
    [rule.ifMissingOutcome],
    LEGACY_MISSING,
    missingVals,
  );

  const invalidDocumentResult = coalesceSingleField(
    rule.invalidDocumentResult,
    [rule.ifRejectedOutcome],
    LEGACY_INVALID,
    invalidVals,
  );

  let expiryReminderTiming = rule.expiryReminderTiming || '';
  if (!expiryReminderTiming && Array.isArray(rule.reminderTiming) && rule.reminderTiming.length) {
    expiryReminderTiming = rule.reminderTiming[0];
  }

  const checkRequired = rule.checkRequired ?? rule.reviewRequired ?? false;
  const checkOutcomeRequired = rule.checkOutcomeRequired ?? Boolean(rule.approvalRequired);

  const out = {
    ...rule,
    ruleArea: rule.ruleArea || rule.module || '',
    entryPoint: normalizeEntryPoint(rule.entryPoint),
    allowedFileFormats: migrateAllowedFormats(rule.allowedFileFormats || []),
    expiredDocumentResult,
    failedDocumentResult,
    missingDocumentResult,
    invalidDocumentResult,
    expiryReminderTiming,
    replacementMethod: normalizeReplacementMethod(rule.replacementMethod),
    moreInformationAllowed: Boolean(rule.moreInformationAllowed),
    checkRequired,
    checkOutcomeRequired,
    uploadAllowed: rule.uploadAllowed !== false,
    multipleUploadAllowed: Boolean(rule.multipleUploadAllowed),
    replacementAllowed: Boolean(rule.replacementAllowed),
    expiryApplies: Boolean(rule.expiryApplies),
  };
  delete out.rejectedDocumentResult;
  delete out.reviewRequired;
  delete out.approvalRequired;
  delete out.internalReviewOnly;
  return out;
}

export function nextDocumentRuleId(rules) {
  const nums = (rules || []).map((r) => {
    const m = String(r.id || '').match(/^DOC-(\d+)$/i);
    return m ? parseInt(m[1], 10) : 0;
  });
  const max = nums.length ? Math.max(...nums) : 0;
  return `DOC-${String(max + 1).padStart(4, '0')}`;
}

export function findDuplicateDocumentRule(rules, moduleValue, entryPointValue, excludeId) {
  if (!moduleValue || !entryPointValue) return null;
  return (rules || []).find(
    (r) =>
      r.id !== excludeId &&
      (r.ruleArea || r.module) === moduleValue &&
      normalizeEntryPoint(r.entryPoint) === normalizeEntryPoint(entryPointValue),
  );
}

/** Build persisted row for list + navigation state */
export function mergeDocumentRuleForList(formData, { id, status, actor, nowIso, isNew }) {
  const ruleArea = formData.ruleArea || '';
  const entryPointVal = normalizeEntryPoint(formData.entryPoint);
  const uploadRule = formatUploadRulesSummary(formData);
  const checkRule = formatCheckRulesSummary({
    ...formData,
    checkRequired: formData.checkRequired,
    checkOutcomeRequired: formData.checkOutcomeRequired,
  });
  const expiryRule = formatExpiryRulesSummary(formData);

  const documentResult = formatDocumentResultColumn({
    ...formData,
    documentResult: '',
    expiredDocumentResult: formData.expiryApplies ? formData.expiredDocumentResult : '',
  });

  const base = {
    ...formData,
    id,
    ruleArea,
    entryPoint: entryPointVal,
    status,
    uploadRule,
    checkRule,
    expiryRule,
    documentResult,
    checkRequired: Boolean(formData.checkRequired),
    checkOutcomeRequired: Boolean(formData.checkOutcomeRequired),
    failedDocumentResult: formData.failedDocumentResult || '',
    createdOn: isNew ? nowIso : formData.createdOn || nowIso,
    createdBy: isNew ? actor : formData.createdBy || actor,
    modifiedOn: nowIso,
    modifiedBy: actor,
  };
  delete base.reviewRequired;
  delete base.approvalRequired;
  delete base.internalReviewOnly;
  delete base.rejectedDocumentResult;
  delete base.reviewRule;
  return base;
}

export function buildSampleDocumentRules() {
  return [
    {
      id: 'DOC-0001',
      ruleName: 'Licence Upload Rule',
      ruleArea: 'compliance',
      entryPoint: 'compliance_application_upload',
      uploadRule: 'PDF, JPG, JPEG, WebP, 1 file, 10 MB max',
      checkRule: 'Check required, check outcome required',
      expiryRule: 'Expiry applies, replacement allowed',
      documentResult: 'Missing or expired document marks item incomplete',
      status: 'active',
      uploadAllowed: true,
      allowedFileFormats: ['pdf', 'jpg', 'jpeg', 'webp'],
      maximumFileSize: '10',
      maximumFileCount: '1',
      multipleUploadAllowed: false,
      checkRequired: true,
      checkOutcomeRequired: true,
      moreInformationAllowed: true,
      failedDocumentResult: 'require_resubmission',
      expiryApplies: true,
      expiryReminderTiming: '30_days_before',
      replacementAllowed: true,
      replacementMethod: 'replace_current_document',
      expiredDocumentResult: 'mark_document_incomplete',
      missingDocumentResult: 'mark_document_incomplete',
      invalidDocumentResult: '',
      createdOn: '2026-01-15T09:00:00+11:00',
      createdBy: 'System',
      modifiedOn: '2026-03-02T14:30:00+11:00',
      modifiedBy: 'Admin User',
    },
    {
      id: 'DOC-0002',
      ruleName: 'Insurance Upload Rule',
      ruleArea: 'compliance',
      entryPoint: 'compliance_renewal_upload',
      uploadRule: 'PDF, JPG, JPEG, WebP, up to 2 files, 10 MB max',
      checkRule: 'Check required, check outcome required',
      expiryRule: 'Expiry applies, replacement allowed',
      documentResult: 'Failed document requires resubmission',
      status: 'active',
      uploadAllowed: true,
      allowedFileFormats: ['pdf', 'jpg', 'jpeg', 'webp'],
      maximumFileSize: '10',
      maximumFileCount: '2',
      multipleUploadAllowed: true,
      checkRequired: true,
      checkOutcomeRequired: true,
      moreInformationAllowed: true,
      failedDocumentResult: 'require_resubmission',
      expiryApplies: true,
      expiryReminderTiming: '14_days_before',
      replacementAllowed: true,
      replacementMethod: 'add_additional_document',
      expiredDocumentResult: 'require_replacement',
      missingDocumentResult: 'mark_document_incomplete',
      invalidDocumentResult: 'show_prompt',
      createdOn: '2026-01-10T11:20:00+11:00',
      createdBy: 'System',
      modifiedOn: '2026-02-18T16:45:00+11:00',
      modifiedBy: 'Admin User',
    },
    {
      id: 'DOC-0003',
      ruleName: 'Message Attachment Rule',
      ruleArea: 'messages',
      entryPoint: 'message_attachment_upload',
      uploadRule: 'PDF, JPG, JPEG, WebP, up to 3 files, 10 MB max',
      checkRule: 'Check optional, check outcome not required',
      expiryRule: 'No expiry, replacement not used',
      documentResult: 'Invalid document shows prompt',
      status: 'active',
      uploadAllowed: true,
      allowedFileFormats: ['pdf', 'jpg', 'jpeg', 'webp'],
      maximumFileSize: '10',
      maximumFileCount: '3',
      multipleUploadAllowed: true,
      checkRequired: false,
      checkOutcomeRequired: false,
      moreInformationAllowed: true,
      failedDocumentResult: '',
      expiryApplies: false,
      expiryReminderTiming: '',
      replacementAllowed: false,
      replacementMethod: '',
      expiredDocumentResult: '',
      missingDocumentResult: 'show_prompt',
      invalidDocumentResult: 'show_prompt',
      createdOn: '2026-02-01T09:00:00+11:00',
      createdBy: 'System',
      modifiedOn: '2026-02-20T12:00:00+11:00',
      modifiedBy: 'Admin User',
    },
    {
      id: 'DOC-0004',
      ruleName: 'Quote Upload Rule',
      ruleArea: 'quotes',
      entryPoint: 'quote_request_upload',
      uploadRule: 'PDF, JPG, JPEG, WebP, up to 5 files, 15 MB max',
      checkRule: 'Check optional, check outcome not required',
      expiryRule: 'No expiry, replacement optional',
      documentResult: 'Missing document no action',
      status: 'draft',
      uploadAllowed: true,
      allowedFileFormats: ['pdf', 'jpg', 'jpeg', 'webp'],
      maximumFileSize: '15',
      maximumFileCount: '5',
      multipleUploadAllowed: true,
      checkRequired: false,
      checkOutcomeRequired: false,
      moreInformationAllowed: true,
      failedDocumentResult: '',
      expiryApplies: false,
      expiryReminderTiming: '',
      replacementAllowed: true,
      replacementMethod: 'replace_current_document',
      expiredDocumentResult: '',
      missingDocumentResult: 'no_action',
      invalidDocumentResult: '',
      createdOn: '2026-04-01T08:00:00+11:00',
      createdBy: 'Admin User',
      modifiedOn: '2026-04-01T08:00:00+11:00',
      modifiedBy: 'Admin User',
    },
    {
      id: 'DOC-0005',
      ruleName: 'Support Reply Upload Rule',
      ruleArea: 'support',
      entryPoint: 'support_ticket_reply_upload',
      uploadRule: 'PDF, JPG, JPEG, WebP, up to 5 files, 15 MB max',
      checkRule: 'Check optional, check outcome not required',
      expiryRule: 'No expiry, replacement optional',
      documentResult: 'File remains supporting evidence only',
      status: 'active',
      uploadAllowed: true,
      allowedFileFormats: ['pdf', 'jpg', 'jpeg', 'webp'],
      maximumFileSize: '15',
      maximumFileCount: '5',
      multipleUploadAllowed: true,
      checkRequired: false,
      checkOutcomeRequired: false,
      moreInformationAllowed: true,
      failedDocumentResult: '',
      expiryApplies: false,
      expiryReminderTiming: '',
      replacementAllowed: true,
      replacementMethod: 'add_additional_document',
      expiredDocumentResult: '',
      missingDocumentResult: 'no_action',
      invalidDocumentResult: '',
      createdOn: '2026-03-10T10:00:00+11:00',
      createdBy: 'System',
      modifiedOn: '2026-03-15T11:30:00+11:00',
      modifiedBy: 'Admin User',
    },
    {
      id: 'DOC-0006',
      ruleName: 'Fraud Evidence Upload Rule',
      ruleArea: 'fraud_security',
      entryPoint: 'fraud_evidence_upload',
      uploadRule: 'PDF, JPG, JPEG, WebP, up to 3 files, 10 MB max',
      checkRule: 'Check required, check outcome required',
      expiryRule: 'No expiry',
      documentResult: 'Failed document requires resubmission',
      status: 'inactive',
      uploadAllowed: true,
      allowedFileFormats: ['pdf', 'jpg', 'jpeg', 'webp'],
      maximumFileSize: '10',
      maximumFileCount: '3',
      multipleUploadAllowed: true,
      checkRequired: true,
      checkOutcomeRequired: true,
      moreInformationAllowed: false,
      failedDocumentResult: 'require_resubmission',
      expiryApplies: false,
      expiryReminderTiming: '',
      replacementAllowed: false,
      replacementMethod: '',
      expiredDocumentResult: '',
      missingDocumentResult: 'no_action',
      invalidDocumentResult: 'mark_document_incomplete',
      createdOn: '2025-11-22T10:15:00+11:00',
      createdBy: 'System',
      modifiedOn: '2026-01-05T09:30:00+11:00',
      modifiedBy: 'Admin User',
    },
  ];
}
